import React, { useState, useEffect } from 'react';
import { Clock, ChevronRight, Loader2, FileText } from 'lucide-react';
import SummaryModal from '../components/SummaryModal';
import { generateSummary } from '../utils/openai';
import { supabase } from '../utils/supabase';
import type { Database } from '../types/supabase';

export type Summary = Database['public']['Tables']['summaries']['Row'] & {
  double: {
    name: string;
  };
  follow_ups: Array<Database['public']['Tables']['follow_ups']['Row']>;
};

// Export empty array as placeholder for components that need the type but not the data
export const summariesData: Summary[] = [];

const Summaries = () => {
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const { data, error } = await supabase
          .from('summaries')
          .select(`
            *,
            double:double_id (
              name
            ),
            follow_ups (*)
          `)
          .order('date', { ascending: false })
          .order('time', { ascending: false });

        if (error) throw error;
        setSummaries(data || []);
      } catch (err) {
        console.error('Error fetching summaries:', err);
        setError('Failed to load summaries');
      } finally {
        setLoading(false);
      }
    };

    fetchSummaries();
  }, []);

  const saveTranscript = async (summaryId: string, content: string) => {
    try {
      const { data, error } = await supabase
        .from('transcripts')
        .insert([
          {
            summary_id: summaryId,
            content,
            user_id: (await supabase.auth.getUser()).data.user?.id
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving transcript:', error);
      throw error;
    }
  };

  // Mock function that used to fetch from ElevenLabs
  const fetchTranscript = async (summaryId: string) => {
    try {
      setTranscriptLoading(summaryId);
      
      // Create mock transcript data
      const mockTranscript = 
        "Agent: Hello! I'm here to help you today. How can I assist you?\n\n" +
        "User: I'd like to discuss our upcoming product launch strategy.\n\n" +
        "Agent: Of course! I'd be happy to discuss the product launch strategy. What aspects would you like to focus on specifically?\n\n" +
        "User: We need to finalize the marketing channels and budget allocation.\n\n" +
        "Agent: Got it. Let's talk about marketing channels and budget allocation. Which channels have you considered so far, and what's your overall marketing budget?";
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Save the mock transcript
      await saveTranscript(summaryId, mockTranscript);
      
      // Generate AI summary
      const aiSummary = await generateSummary(mockTranscript);
      
      // Update the summary in Supabase
      const { error: updateError } = await supabase
        .from('summaries')
        .update({
          summary: aiSummary.summary,
          key_points: aiSummary.keyPoints
        })
        .eq('id', summaryId);

      if (updateError) throw updateError;

      // Insert new follow-ups
      if (aiSummary.followUps.length > 0) {
        const { error: followUpsError } = await supabase
          .from('follow_ups')
          .insert(
            aiSummary.followUps.map(followUp => ({
              summary_id: summaryId,
              title: followUp.title,
              description: followUp.description,
              status: 'pending',
              assignee: followUp.assignee || 'Not assigned',
              due_date: followUp.dueDate
            }))
          );

        if (followUpsError) throw followUpsError;
      }
      
      // Refresh the summary data
      const { data: updatedSummary, error } = await supabase
        .from('summaries')
        .select(`
          *,
          double:double_id (
            name
          ),
          follow_ups (*)
        `)
        .eq('id', summaryId)
        .single();

      if (error) throw error;
      
      // Update the selected summary
      setSelectedSummary(updatedSummary);
      
      // Refresh all summaries
      const { data: refreshedSummaries, error: refreshError } = await supabase
        .from('summaries')
        .select(`
          *,
          double:double_id (
            name
          ),
          follow_ups (*)
        `)
        .order('date', { ascending: false })
        .order('time', { ascending: false });

      if (refreshError) throw refreshError;
      setSummaries(refreshedSummaries || []);
      
    } catch (error) {
      console.error('Error with transcript processing:', error);
    } finally {
      setTranscriptLoading(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Meeting Summaries</h1>
        <p className="text-gray-600">Review conversations and decisions made by your doubles.</p>
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading summaries...</p>
        </div>
      ) : summaries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-gray-600">No summaries found. They will appear here after your doubles complete their meetings.</p>
        </div>
      ) : (
        <div className="space-y-4">
        {summaries.map((summary) => (
          <div
            key={summary.id}
            className="w-full text-left bg-white p-6 rounded-lg border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
          >
            <div className="flex items-start justify-between">
              <div 
                className="flex-1 cursor-pointer"
                onClick={() => setSelectedSummary(summary)}
              >
                <h3 className="font-semibold text-gray-900">{summary.title}</h3>
                <div className="flex items-center gap-4 text-sm text-gray-600 mt-1 mb-2">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>
                      {new Date(summary.date).toLocaleDateString()} at {summary.time} • {summary.double.name}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 line-clamp-2">{summary.summary}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => fetchTranscript(summary.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    transcriptLoading === summary.id
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  disabled={transcriptLoading === summary.id}
                >
                  {transcriptLoading === summary.id ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <FileText className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={() => setSelectedSummary(summary)}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div 
              className="mt-4 cursor-pointer"
              onClick={() => setSelectedSummary(summary)}>
              <div className="flex flex-wrap gap-2">
                {(summary.key_points || []).slice(0, 2).map((point, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                  >
                    {point}
                  </span>
                ))}
                {(summary.key_points?.length || 0) > 2 && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                    +{(summary.key_points?.length || 0) - 2} more
                  </span>
                )}
              </div>
            </div>
          </div>
          ))}
        </div>
      )}

      <SummaryModal
        summary={selectedSummary}
        onClose={() => setSelectedSummary(null)}
      />
    </div>
  );
};

export default Summaries;